import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import debounce from 'lodash.debounce';
import {MessageContext} from 'src/contexts/message-context';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectUserShortInfo} from 'src/modules/main/services/user-info-slice';
import {runGeneralSearch} from 'src/openapi/admin/apis/RunGeneralSearchApi';
import {useAppDispatch, useAppSelector} from 'src/store/hooks';

import {Autosuggest, AutosuggestItem, AutosuggestNoResults} from '@pb/kit-component-autosuggest-v2';
import {FlatButton} from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {Dropdown} from '@pb/kit-component-dropdown';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {ListboxGroup} from '@pb/kit-component-listbox';
import Loading, {Types as LoadingTypes} from '@pb/kit-component-loading';

import {useGeneralSearchShowMore} from './hooks/useGeneralSearchShowMore';
import {SuggestionSection} from './typedef';
import {filterSuggestionSections, getSectionSearchType, isNumberQuery, mapGeneralSearchResponse} from './utils';

import type {Error} from 'src/openapi/admin/models/Error';


// eslint-disable-next-line no-useless-escape
const SEARCH_REGEX = /[^a-zA-Z0-9 &'.+_@,\(\)\[\]-]+/g;
const MINIMUM_QUERY_ERROR = 'The search query is less than the minimum allowed, continue typing...';
const INVALID_QUERY_ERROR = 'The search query contains invalid characters, please remove them and try again...';

const DEBOUNCE_DELAY = 300;

type Props = {
    children: React.ReactNode,
};

const Body = ({children}: Props) => (
    <div className="pdy-sm pdx-m">
        {children}
    </div>
);

export const GeneralSearch = () => {
    const navigate = useNavigate();
    const apiDispatch = useAppDispatch();
    const {hasAccess} = useAdminAccessRight();
    const {setErrorMessage} = useContext(MessageContext);
    const userShortInfo = useAppSelector(selectUserShortInfo);

    const [query, setQuery] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState<Error | null>(null);
    const [suggestionsData, setSuggestionsData] = useState<SuggestionSection[]>([]);
    const [shown, setShown] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [onlyMyAccounts, setOnlyMyAccounts] = useState(false);

    const {accountProfileOpen, userProfileOpen} = useEventTracking();

    const currentRequestRef = useRef<number>(0);

    const sanitizedQuery = useMemo(
        () => query.replace(SEARCH_REGEX, ''),
        [query],
    );

    const performSearch = useCallback(async (searchQuery: string, myAccountsOnly: boolean) => {
        // Increment request counter to cancel previous requests
        currentRequestRef.current += 1;
        const requestId = currentRequestRef.current;

        setLoading(true);
        setLoadingError(null);

        try {
            const res = await apiDispatch(runGeneralSearch({
                generalSearchRequest: {
                    searchCriteria: searchQuery.trim(),
                    onlyMyAccounts: myAccountsOnly,
                },
            }));

            if (requestId === currentRequestRef.current) {
                if (res.payload.success) {
                    const mappedSuggestions = mapGeneralSearchResponse(res.payload.data);
                    setSuggestionsData(mappedSuggestions);
                    setLoadingError(null);
                } else if (res.payload.error) {
                    setLoadingError(res.payload.data);
                    setSuggestionsData([]);
                }
            }
        } catch (error) {
            if (requestId === currentRequestRef.current) {
                console.error('Search error:', error);
                setSuggestionsData([]);
            }
        } finally {
            if (requestId === currentRequestRef.current) {
                setLoading(false);
            }
        }
    }, [apiDispatch]);

    const debouncedSearch = useMemo(
        () => debounce((searchQuery: string, myAccountsOnly: boolean) => {
            const shouldSearch = myAccountsOnly || isNumberQuery(searchQuery) || searchQuery.trim().length > 2;

            if (shouldSearch) {
                performSearch(searchQuery, myAccountsOnly);
            } else {
                setSuggestionsData([]);
                setLoading(false);
            }
        }, DEBOUNCE_DELAY),
        [performSearch],
    );

    useEffectWithDeps((prev, current) => {
        const queryChanged = prev.sanitizedQuery.trim() !== current.sanitizedQuery.trim();
        const accountsFilterChanged = prev.onlyMyAccounts !== current.onlyMyAccounts;

        if (queryChanged || accountsFilterChanged) {
            debouncedSearch(current.sanitizedQuery, current.onlyMyAccounts);
        }
    }, {sanitizedQuery, onlyMyAccounts});

    // Cleanup effect
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const clearSuggestions = useCallback(() => {
        // Cancel any pending debounced requests
        debouncedSearch.cancel();
        currentRequestRef.current += 1;

        setQuery('');
        setSuggestionsData([]);
        setShown(false);
        setLoading(false);
    }, [debouncedSearch]);

    const afterShowMoreNavigate = useCallback(() => {
        clearSuggestions();
        setIsOpen(false);
    }, [clearSuggestions]);

    const handleShowMore = useGeneralSearchShowMore({
        query: sanitizedQuery,
        onAfterNavigate: afterShowMoreNavigate,
        onlyMyAccounts,
        userShortInfo,
    });

    const onItemSelect = useCallback((item: {additionData: {searchType: string}, id: string | number}) => {
        setShown(false);
        if (item.additionData && item.additionData.searchType === 'USER') {
            if (hasAccess(ACCESS_RIGHTS.USER_SCREEN_VIEW)) {
                navigate(`/profile/user/${item.id}/user-details`);
                userProfileOpen({place: 'Global Search'});
            } else {
                setErrorMessage('Access denied. Please contact your manager.');
            }
        }
        if (item.additionData && item.additionData.searchType === 'ACCOUNT') {
            if (hasAccess(ACCESS_RIGHTS.ACCOUNT_SCREEN_VIEW)) {
                navigate(`/profile/account/${item.id}`);
                accountProfileOpen({place: 'Global Search'});
            } else {
                setErrorMessage('Access denied. Please contact your manager.');
            }
        }
        clearSuggestions();
        setIsOpen(false);
    }, [accountProfileOpen, clearSuggestions, hasAccess, navigate, setErrorMessage, userProfileOpen]);

    const focusAutoSuggest = useCallback(() => {
        if (onlyMyAccounts) {
            setIsOpen(true);
            debouncedSearch(sanitizedQuery, true);
        }
    }, [onlyMyAccounts, debouncedSearch, sanitizedQuery]);

    const blurAutoSuggest = useCallback(() => {
        if (onlyMyAccounts) {
            setIsOpen(false);
            setQuery('');
        }
    }, [onlyMyAccounts]);

    const autosuggestError = useMemo(() => {
        if (sanitizedQuery !== query) {
            return INVALID_QUERY_ERROR;
        } else if (query.trim().length < 3 && !isNumberQuery(query) && !onlyMyAccounts) {
            return MINIMUM_QUERY_ERROR;
        } else if (loadingError) {
            return loadingError.message;
        } else if (isLoading) {
            return 'loading...';
        }
        return null;
    }, [query, loadingError, sanitizedQuery, isLoading, onlyMyAccounts]);


    const renderedSuggestions = useMemo(() => {
        if (autosuggestError) {
            return [
                <div key="error" className="pdy-sm pdx-m">
                    {autosuggestError}
                </div>,
            ];
        } else if (suggestionsData.length > 0) {
            const results = filterSuggestionSections(query, suggestionsData).flatMap(({section, filteredItems}) => {
                const searchType = getSectionSearchType(section.title);
                const isShowMoreBtnShown = section.suggestionItems.length >= 10;

                const caption = (
                    <Group justify="space-between" alignItems="center">
                        <span>{section.title}</span>
                        {isShowMoreBtnShown ? (
                            <FlatButton
                                disabled={false}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    handleShowMore(searchType);
                                }}>
                                {`Show more ${section.title.toLowerCase()}`}
                            </FlatButton>
                        ) : null}
                    </Group>
                );

                return [
                    <ListboxGroup key={section.title} caption={caption}>
                        {filteredItems.map((item, index) => (
                            <AutosuggestItem
                                key={`${item.id}-${index}`}
                                value={item}
                                caption={item.name}
                                disabled={item.disabled}
                            />
                        ))}
                    </ListboxGroup>,
                ];
            });

            if (results.length === 0) {
                return [<AutosuggestNoResults key="no-results"/>];
            }

            return results;
        } else if (query.trim().length > 0 && !isLoading) {
            return [<AutosuggestNoResults key="no-results"/>];
        }
        return [];
    }, [query, suggestionsData, autosuggestError, isLoading, handleShowMore]);

    const shouldShowPopover = onlyMyAccounts ? !isOpen : !query;

    const endAdornment = useMemo(() => (
        <Group gap="s" alignItems="center">
            {isLoading ? <Loading type={LoadingTypes.ICON} fast={true}/> : null}
            {shouldShowPopover ? (
                <Dropdown
                    hideOnBodyLeave={false}
                    verticalOffset={4}
                    arrowProps={{padding: 14}}
                    bodyComponent={Body}
                    placement="bottom-start"
                    anchor={({anchorRef, getAnchorProps}) => (
                        <BasicIconButton
                            ref={anchorRef}
                            {...getAnchorProps()}
                            iconProps={{type: Icons.COGS}}
                        />
                    )}>
                    <CheckBox
                        label="Only My Accounts"
                        checked={onlyMyAccounts}
                        onCheckBoxChange={data => {
                            setOnlyMyAccounts(data.event.currentTarget.checked);
                        }}
                    />
                </Dropdown>
            ) : null}
            <Icon type={Icons.SEARCH}/>
        </Group>
    ), [isLoading, shouldShowPopover, onlyMyAccounts]);

    return (
        <div style={{width: 400}}>
            <Autosuggest
                value={query}
                type="ABSOLUTE"
                maxLength={255}
                shown={shown}
                setShown={setShown}
                onFocus={focusAutoSuggest}
                onBlur={blurAutoSuggest}
                onClear={() => {
                    setIsOpen(false);
                    clearSuggestions();
                }}
                onChange={e => setQuery(e.target.value)}
                placeholder="Enter Account name, User email or ID"
                endAdornment={endAdornment}
                onItemSelect={onItemSelect}>
                {renderedSuggestions}
            </Autosuggest>
        </div>
    );
};
